import { LoginTwoTone, Visibility, VisibilityOff, LockOutlined } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Avatar,
  Grid,
  Link as MuiLink,
  Stack,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import pages from 'src/router/routes';
import Logo from 'src/components/LogoSign';

// Zod validation schema
const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Must be a valid email address'),
  password: z
    .string()
    .min(6, 'Password must be at least 6 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
});

type LoginFormData = {
  email: string;
  password: string;
};
export const Login = () => {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);

  const { loginWithEmailAndPassword, isAuthenticated, loading } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleClickShowPassword = () => setShowPassword((prevState) => !prevState);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const onSubmit: SubmitHandler<LoginFormData> = async (data) => {
    try {
      await loginWithEmailAndPassword(data.email, data.password);
    } catch {
      // Firebase errors are handled in the FirebaseAuthProvider context
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Box sx={{ position: 'absolute', left: 0, top: 0 }}>
        <Logo width={150} />
      </Box>

      <Container maxWidth='sm'>
        <Card sx={{ p: 3 }}>
          <Stack alignItems='center' m={1} spacing={1}>
            <Avatar sx={{ m: 1, bgcolor: 'primary.main', width: 56, height: 56 }}>
              <LockOutlined fontSize='large' />
            </Avatar>
            <Typography variant='h4'>Login</Typography>
          </Stack>

          <CardContent>
            <Box component='form' onSubmit={handleSubmit(onSubmit)} noValidate autoComplete='off'>
              <Grid container spacing={2} direction='column'>
                <Grid item>
                  <TextField
                    required
                    fullWidth
                    id='email'
                    label='Email Address'
                    autoComplete='email'
                    disabled={loading || isSubmitting}
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                </Grid>

                <Grid item>
                  <TextField
                    required
                    fullWidth
                    id='password'
                    label='Password'
                    type={showPassword ? 'text' : 'password'}
                    autoComplete='current-password'
                    disabled={loading || isSubmitting}
                    {...register('password')}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <Tooltip title={showPassword ? 'Hide Password' : 'Show Password'}>
                            <IconButton
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                              onClick={handleClickShowPassword}
                              edge='end'
                              disabled={loading || isSubmitting}
                            >
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item>
                  <LoadingButton
                    loading={loading || isSubmitting}
                    disabled={loading || isSubmitting}
                    fullWidth
                    variant='contained'
                    color='primary'
                    type='submit'
                    size='large'
                    startIcon={<LoginTwoTone />}
                  >
                    Sign In
                  </LoadingButton>
                </Grid>

                <Grid item display='flex' justifyContent='flex-end'>
                  <MuiLink
                    component={Link}
                    to={pages.auth.forgotPassword.path}
                    variant='body2'
                    underline='hover'
                    sx={{ fontWeight: 500 }}
                  >
                    Forgot your password?
                  </MuiLink>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};
